import styled from '@emotion/styled';
import { tokens } from '@hotjar/hotsauce';
import { useTranslation } from 'react-i18next';
import { ListButton } from '@hotjar/navigation/src/components/SidebarList';

import type { ReactNode } from 'react';

export interface SidebarProps {
  className?: string;
  isCollapsed: boolean;
  onCollapseClick: () => void;
  children: ReactNode;
  showCollapseToggle: boolean;
}

const Container = styled.aside<{ isCollapsed: boolean }>`
  border-right: 1px solid ${tokens.color.ui_border_light};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: ${tokens.spacer.x1pt5};
  width: ${({ isCollapsed }) => (isCollapsed ? tokens.spacer.x8 : '220px')};
  background-color: ${tokens.color.ui_background};
  overflow-y: auto;
  overflow-x: hidden;

  ${tokens.transition('width', 300)};
`;

const StyledListItem = styled(ListButton)`
  min-height: ${tokens.spacer.x5};
  margin-top: ${tokens.spacer.x0};
`;

export const SideBar = ({
  className,
  isCollapsed,
  onCollapseClick,
  children,
  showCollapseToggle,
}: SidebarProps): JSX.Element => {
  const { t } = useTranslation('navigation');
  return (
    <Container aria-label={t('SideBar.label')} className={className} isCollapsed={isCollapsed}>
      {children}
      {showCollapseToggle && (
        <StyledListItem
          icon={isCollapsed ? 'expand_right' : 'expand_left'}
          text={isCollapsed ? t('SideBar.expand') : t('SideBar.collapse')}
          isCollapsed={isCollapsed}
          onClick={onCollapseClick}
        />
      )}
    </Container>
  );
};
